import { db } from '../firebase';
import {
  doc, getDoc, setDoc, serverTimestamp,
  collection, query, where, limit, getDocs,
} from 'firebase/firestore';
import { DEFAULT_TIER } from '../config/tierDefaults';

// ── User account ──────────────────────────────────────────────────────────────

/**
 * Fetch the user's account doc. Creates one with Basic defaults on first login.
 * Returns { tier, scanCreditsExtra, isAdmin } or the default if anything fails.
 */
export async function getOrCreateAccount(uid) {
  if (!uid) return null;
  const ref = doc(db, 'users', uid, 'meta', 'account');
  try {
    const snap = await getDoc(ref);
    if (snap.exists()) return snap.data();
    const accountData = {
      tier: DEFAULT_TIER,
      scanCreditsExtra: 0,
      isAdmin: false,
      createdAt: serverTimestamp(),
    };
    await setDoc(ref, accountData);
    return { tier: DEFAULT_TIER, scanCreditsExtra: 0, isAdmin: false };
  } catch (err) {
    console.error('getOrCreateAccount failed:', err);
    return { tier: DEFAULT_TIER, scanCreditsExtra: 0, isAdmin: false };
  }
}

/**
 * Partial update of the account doc (merge). Used by the admin panel.
 */
export async function updateAccountDoc(uid, fields) {
  if (!uid) return;
  await setDoc(doc(db, 'users', uid, 'meta', 'account'), fields, { merge: true });
}

// ── Monthly scan usage ────────────────────────────────────────────────────────

/**
 * Read the current month's scan usage. Returns null if no record exists yet.
 */
export async function getUsageDoc(uid) {
  if (!uid) return null;
  try {
    const snap = await getDoc(doc(db, 'users', uid, 'meta', 'usage'));
    return snap.exists() ? snap.data() : null;
  } catch (err) {
    console.error('getUsageDoc failed:', err);
    return null;
  }
}

// ── Tier config ───────────────────────────────────────────────────────────────

/**
 * Fetch live tier limits from Firestore config/tiers.
 * Returns null on failure — callers should fall back to TIER_DEFAULTS.
 */
export async function getTierConfig() {
  try {
    const snap = await getDoc(doc(db, 'config', 'tiers'));
    return snap.exists() ? snap.data() : null;
  } catch (err) {
    console.error('getTierConfig failed:', err);
    return null;
  }
}

/**
 * Overwrite the entire config/tiers document. Admin use only.
 */
export async function saveTierConfig(tierData) {
  await setDoc(doc(db, 'config', 'tiers'), tierData);
}

// ── LLM model config ─────────────────────────────────────────────────────────

export async function getLLMConfig() {
  try {
    const snap = await getDoc(doc(db, 'config', 'llm'));
    return snap.exists() ? snap.data() : {};
  } catch (err) {
    console.error('getLLMConfig failed:', err);
    return {};
  }
}

export async function saveLLMConfig(data) {
  await setDoc(doc(db, 'config', 'llm'), data);
}

// ── Public pricing config ─────────────────────────────────────────────────────

/**
 * Fetch pricing display config from publicData/pricing.
 * Readable without auth. Returns null if missing or on error.
 */
export async function getPricingConfig() {
  try {
    const snap = await getDoc(doc(db, 'publicData', 'pricing'));
    return snap.exists() ? snap.data() : null;
  } catch (err) {
    console.error('getPricingConfig failed:', err);
    return null;
  }
}

/**
 * Overwrite publicData/pricing. Admin use only.
 */
export async function savePricingConfig(pricingData) {
  await setDoc(doc(db, 'publicData', 'pricing'), pricingData);
}

// ── Admin: user search ────────────────────────────────────────────────────────

/**
 * Prefix search on usersByEmail. Returns up to 20 matching user records.
 * Requires the Firestore index on usersByEmail.email (ascending).
 */
export async function searchUsersByEmail(emailPrefix) {
  if (!emailPrefix) return [];
  try {
    const prefix = emailPrefix.toLowerCase();
    const q = query(
      collection(db, 'usersByEmail'),
      where('email', '>=', prefix),
      where('email', '<=', prefix + ''),
      limit(20)
    );
    const snap = await getDocs(q);
    return snap.docs.map(d => d.data());
  } catch (err) {
    console.error('searchUsersByEmail failed:', err);
    return [];
  }
}
